import { push } from 'redux-first-history';
import { all, takeEvery, put, call } from 'redux-saga/effects';
import actions from './actions';
import Api from '../../../../helpers/api';
import { sendRequest } from '../../../../helpers/saga';
import { logger } from '../../../../helpers/logger';
import { isAUS } from '../../../../helpers/countriesHelpers';
import notifications from '../../../../redux/notifications/actions';
import { skillsKeysUSA } from '../../../../helpers/skillMapper';
import { t } from '../../../../i18n';

const api = new Api('positions/:id');

function setValues(obj) {
  // eslint-disable-next-line no-restricted-syntax
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] && obj[key].value !== undefined) {
      obj[key] = obj[key].value;
    }
  }

  return obj;
}

function formatPosition(pos, { isCreate = false } = {}) {
  const position = { ...pos, skills: [...pos.skills.map(skill => ({ ...skill }))] };
  // `job_type` is a read-only display object.
  const fieldsToRemove = [
    'talents_count',
    'past_jobs_count',
    'future_jobs_count',
    'fulfillment',
    'assessment',
    'job_type',
  ];

  // Create-only. Stripped rather than assumed absent: `onEdit` spreads the whole show response into
  // the form, so the day it carries `job_type_id`, every edit would send it.
  if (!isCreate) {
    fieldsToRemove.push('job_type_id');
  }

  if (!position.assessment.value) {
    fieldsToRemove.push('passing_score');
    position.skills = position.skills.map(skill => ({
      id: skill.id,
      description: skill.description,
      level: skill.level,
      requirement_description: skill.requirement_description,
      tasks_description: skill.tasks_description,
      // eslint-disable-next-line no-underscore-dangle
      ...(skill._destroy && skill._destroy !== 0 && { _destroy: { ...skill._destroy } }),
    }));
  }

  fieldsToRemove.map(key => delete position[key]);

  // eslint-disable-next-line no-restricted-syntax
  for (const key in position) {
    if (key === 'skills') {
      position.skills_attributes = position.skills.map(setValues).map((item, index) => {
        item.level = isAUS() ? `tier_${index + 1}` : skillsKeysUSA[index];

        return item;
      });
      delete position.skills;
    } else if (key === 'country') {
      setValues(position[key]);
    } else {
      setValues(position);
    }
  }

  return position;
}

export function* loadData({ payload }) {
  try {
    const result = yield call(sendRequest, api.load, { id: payload.id }, notifications.REQUEST_ERROR);

    if (result && result.data && result.data.success) {
      yield put(actions.loadDataSuccess({ data: { position: result.data.success.data } }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function jobTypesErrorNotification() {
  return notifications.addNotification({
    headline: t('dash.common.notification.error_headline'),
    type: 'error',
    message: t('dash.common.notification.something_went_wrong'),
  });
}

export function* loadFormData() {
  try {
    const result = yield call(sendRequest, api.loadFormData, {}, notifications.REQUEST_ERROR);
    const jobTypes = result?.data?.success?.data?.job_types;

    // A missing key is a failure, not an empty catalogue: the form would demand a job type it cannot
    // offer.
    if (!Array.isArray(jobTypes)) {
      yield put(actions.loadFormDataError());

      if (result) {
        yield put(jobTypesErrorNotification());
      }

      return;
    }

    yield put(actions.loadFormDataSuccess({ job_types: jobTypes }));
  } catch (error) {
    logger.error(error);
    yield put(actions.loadFormDataError());
    yield put(jobTypesErrorNotification());
  }
}

export function* addPosition({ payload }) {
  try {
    const position = payload.editForm;
    const result = yield call(
      sendRequest,
      api.createItem,
      {
        data: { position: formatPosition(position, { isCreate: true }) },
      },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      const { id } = result.data;
      yield put(actions.saveSuccess({}));
      yield put(actions.loadData({ id }));
      yield put(push(`/positions/${id}`));

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.created_successfully', {
            item: t('dash.admin.positions.position_title'),
          }),
        }),
      );
    } else {
      yield put(actions.saveFail({ data: result }));
    }
  } catch (error) {
    logger.error(error);
  }
}

export function* updatePosition({ payload }) {
  try {
    const { editForm, id } = payload;
    const result = yield call(
      sendRequest,
      api.updateItem,
      {
        data: { position: formatPosition(editForm) },
        id,
      },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield put(actions.saveSuccess({}));
      yield put(actions.loadData({ id }));
      yield put(push(`/positions/${id}`));

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.positions.position_title'),
          }),
        }),
      );
    } else {
      yield put(actions.saveFail({ data: result }));
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD, loadData),
    takeEvery(actions.LOAD_FORM_DATA, loadFormData),
    takeEvery(actions.ADD, addPosition),
    takeEvery(actions.SAVE, updatePosition),
  ]);
}
